import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Paper, Typography, Button, Breadcrumbs } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddIcon from "@mui/icons-material/Add";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import DataTable from "datatables.net-bs5";
import $ from "jquery";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import "select2/dist/css/select2.min.css";
import "select2";
import Swal from "sweetalert2";
import "./RaiseComplaint.css";
import "./ComplaintCategoryMasterManagement.css";
import { complaintCategoryMasterAPI } from "../services/api";
import { getRoutePermissions } from "../utils/auth";
import { useLocation } from "react-router-dom";

const normalizeComplaintCategory = (item) => ({
  compCatgCode:
    item.compCatgCode || item.CompCatgCode || item.COMPCATGCODE || "",
  compCatgId:
    item.compCatgId || item.CompCatgId || item.COMPCATGID || "",
  compCatgName:
    item.compCatgName || item.CompCatgName || item.COMPCATGNAME || "",
  isActive: item.isActive ?? item.IsActive ?? false,
  createdOn: item.createdOn || item.CreatedOn || null,
});

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const ComplaintCategoryMasterManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber] = useState(1);
  const [pageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const tableRef = useRef(null);
  const dataTableInstanceRef = useRef(null);
  const statusSelectRef = useRef(null);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  // Dynamic privilege checks (backend-driven via user menus)
  const routePermissions = getRoutePermissions(location.pathname);
  const { canView, canAdd, canEdit, canDelete } = routePermissions;
  
  // Debug logging
  if (process.env.NODE_ENV === 'development') {
    console.log('Component Category - Permission Checks:', {
      path: location.pathname,
      canView,
      canAdd,
      canEdit,
      canDelete,
      routePermissions,
      user: currentUser,
      privilegeDetails: currentUser?.PrivilegeDetails || currentUser?.privilegeDetails
    });
  }

  useEffect(() => {
    if (!canView) {
      Swal.fire({
        title: "Access Denied",
        text: "You do not have permission to view this page.",
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => {
        navigate("/dashboard");
      });
      return;
    }
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize Select2 for Status dropdown
  useEffect(() => {
    if (statusSelectRef.current) {
      const $select = $(statusSelectRef.current);

      if ($select.data("select2")) {
        $select.select2("destroy");
      }

      $select.select2({
        width: "100%",
        minimumResultsForSearch: Infinity,
      });

      $select.on("change", function (e) {
        handleFilterChange("status", e.target.value);
      });

      return () => {
        if ($select.data("select2")) {
          $select.select2("destroy");
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update Select2 value when filters.status changes
  useEffect(() => {
    if (statusSelectRef.current && $(statusSelectRef.current).data("select2")) {
      const $select = $(statusSelectRef.current);
      const currentValue = $select.val();
      if (currentValue !== filters.status) {
        $select.off("change");
        $select.val(filters.status).trigger("change.select2");
        $select.on("change", function (e) {
          handleFilterChange("status", e.target.value);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status]);

  useEffect(() => {
    if (!tableRef.current) return;

    if (dataTableInstanceRef.current) {
      dataTableInstanceRef.current.destroy();
      dataTableInstanceRef.current = null;
    }

    try {
      if (!$.fn.DataTable) {
        DataTable($);
      }

      dataTableInstanceRef.current = $(tableRef.current).DataTable({
        data: categories,
        paging: true,
        searching: true,
        info: true,
        lengthChange: true,
        lengthMenu: [5, 10, 25, 50, 100],
        dom: "lfrtip",
        ordering: true,
        destroy: true,
        scrollX: true,
        autoWidth: false,
        columns: [
          {
            title: "SNo.",
            data: null,
            render: (d, type, row, meta) =>
              (pageNumber - 1) * pageSize + meta.row + 1,
            className: "text-center",
          },
          {
            title: "Category ID",
            data: null,
            render: (d, type, row) => {
              const id = row.compCatgId || row.CompCatgId || row.COMPCATGID || row.COMPONANTCATGID || "";
              return id || "-";
            },
          },
          { 
            title: "Category Code", 
            data: "compCatgCode",
            visible: false
          },
          { title: "Category Name", data: "compCatgName" },
          {
            title: "Created On",
            data: "createdOn",
            render: (d) => formatDateTime(d),
          },
          {
            title: "Status",
            data: null,
            orderable: false,
            render: (d) => {
              const isActive = d.isActive === true || d.isActive === 1;
              const toggleClass = isActive ? "active" : "inactive";
              const toggleIcon = isActive ? "bi-toggle-on" : "bi-toggle-off";
              return `<div class="d-flex justify-content-center">
                <button class="btn btn-sm status-toggle ${toggleClass}" 
                  data-code="${d.compCatgCode}" 
                  data-active="${isActive}"
                  title="${
                    isActive ? "Click to deactivate" : "Click to activate"
                  }">
                  <i class="bi ${toggleIcon}"></i>
                </button>
              </div>`;
            },
          },
          {
            title: "Action",
            data: null,
            orderable: false,
            searchable: false,
            render: (d) => {
              const editButton = canEdit
                ? `<button class="btn btn-sm btn-edit-category edit-category" data-code="${d.compCatgCode}" title="Edit Component Category">
                  <i class="bi bi-pencil-fill"></i>
                </button>`
                : "";
              const deleteButton = canDelete
                ? `<button class="btn btn-sm btn-delete-category delete-category" data-code="${d.compCatgCode}" title="Delete Component Category">
                  <i class="bi bi-trash-fill"></i>
                </button>`
                : "";
              return `<div class="d-flex justify-content-center gap-2">${editButton}${deleteButton}</div>`;
            },
          },
        ],
        initComplete: function () {
          try {
            const wrapper = $(tableRef.current).closest(
              ".dataTables_wrapper, .dt-container"
            );
            if (wrapper.length > 0) {
              const lengthEl = wrapper.find(".dt-length");
              const searchEl = wrapper.find(".dt-search");
              if (lengthEl.length > 0 || searchEl.length > 0) {
                let topWrap = wrapper.find(".dtFltrTop");
                if (topWrap.length === 0) {
                  topWrap = $("<div class='dtFltrTop d-flex flex-wrap'></div>");
                  wrapper.prepend(topWrap);
                }
                lengthEl.each(function () {
                  $(this).appendTo(topWrap);
                });
                searchEl.each(function () {
                  $(this).appendTo(topWrap);
                });
              }

              const infoEl = wrapper.find(".dt-info");
              const pagingEl = wrapper.find(".dt-paging");
              if (infoEl.length > 0 || pagingEl.length > 0) {
                let bottomWrap = wrapper.find(".dtFltrBotm");
                if (bottomWrap.length === 0) {
                  bottomWrap = $(
                    "<div class='dtFltrBotm d-flex flex-wrap'></div>"
                  );
                  wrapper.append(bottomWrap);
                }
                infoEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
                pagingEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
              }
            }
          } catch (error) {
            console.error("Error in initComplete:", error);
          }
        },
      });

      $(tableRef.current)
        .off("click", ".edit-category")
        .on("click", ".edit-category", function () {
          if (!canEdit) {
            Swal.fire({
              title: "Access Denied",
              text: "You do not have permission to edit complaint categories.",
              icon: "error",
            });
            return;
          }
          const code = $(this).data("code");
          if (code) navigate(`/component-categories/edit/${code}`);
        });

      $(tableRef.current)
        .off("click", ".delete-category")
        .on("click", ".delete-category", function () {
          if (!canDelete) {
            Swal.fire({
              title: "Access Denied",
              text: "You do not have permission to delete complaint categories.",
              icon: "error",
            });
            return;
          }
          const code = $(this).data("code");
          if (code) handleDelete(code);
        });

      $(tableRef.current)
        .off("click", ".status-toggle")
        .on("click", ".status-toggle", function () {
          const code = $(this).data("code");
          const currentActive =
            $(this).data("active") === true ||
            $(this).data("active") === "true" ||
            $(this).data("active") === 1;
          handleStatusToggle(code, !currentActive);
        });
    } catch (err) {
      console.error("DataTable init error", err);
    }

    return () => {
      if (dataTableInstanceRef.current) {
        dataTableInstanceRef.current.destroy();
        dataTableInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  const fetchCategories = async (
    search = filters.search,
    status = filters.status
  ) => {
    setLoading(true);
    try {
      const response = await complaintCategoryMasterAPI.getPaged({
        searchText: search || undefined,
        status: status === "all" ? undefined : status,
        pageNumber: pageNumber,
        pageSize: pageSize,
      });

      if (response?.success && response.data) {
        const payload = response.data;
        const items = payload.categories || payload.Categories || [];
        setCategories(items.map((cat) => normalizeComplaintCategory(cat)));
        setTotalRecords(payload.totalRecords || payload.TotalRecords || 0);
      } else {
        setCategories([]);
        setTotalRecords(0);
      }
    } catch (e) {
      console.error("Error fetching complaint categories:", e);
      setCategories([]);
      Swal.fire(
        "Error",
        "Failed to load complaint categories. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({ search: "", status: "all" });
    fetchCategories("", "all");
  };

  const handleStatusToggle = async (compCatgCode, newActive) => {
    const actionText = newActive ? "activate" : "deactivate";
    const result = await Swal.fire({
      title: "Confirm Status Change",
      text: `Are you sure you want to ${actionText} this component category?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: newActive ? "#4caf50" : "#ff9800",
      cancelButtonColor: "#6c757d",
      confirmButtonText: newActive ? "Yes, activate!" : "Yes, deactivate!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const modifiedBy = currentUser?.userIdCode || "SYSADMIN001";
        const response = await complaintCategoryMasterAPI.updateStatus(
          compCatgCode,
          newActive,
          modifiedBy
        );

        if (response?.success) {
          Swal.fire(
            "Success!",
            response.message || `Component category has been ${actionText}d.`,
            "success"
          );
          fetchCategories();
        } else {
          Swal.fire(
            "Error",
            response?.message || `Failed to ${actionText} component category`,
            "error"
          );
        }
      } catch (e) {
        console.error(`Error ${actionText}ing component category:`, e);
        Swal.fire(
          "Error",
          e.response?.data?.message ||
            `Failed to ${actionText} component category. Please try again.`,
          "error"
        );
      }
    }
  };

  const handleDelete = async (compCatgCode) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will deactivate the component category.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, proceed!",
    });

    if (result.isConfirmed) {
      try {
        const modifiedBy = currentUser?.userIdCode || "SYSADMIN001";
        const response = await complaintCategoryMasterAPI.delete(
          compCatgCode,
          modifiedBy
        );

        if (response?.success) {
          Swal.fire(
            "Updated!",
            response.message || "Component category has been deactivated.",
            "success"
          );
          fetchCategories();
        } else {
          Swal.fire(
            "Error",
            response?.message || "Failed to update component category",
            "error"
          );
        }
      } catch (e) {
        console.error("Error deleting component category:", e);
        Swal.fire(
          "Error",
          e.response?.data?.message ||
            "Failed to update component category. Please try again.",
          "error"
        );
      }
    }
  };

  if (!canView) {
    return null;
  }

  return (
    <Box>
      <Box className="complaint-category-header">
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            className="add-edit-product-breadcrumb"
          >
            <Typography
              color="text.primary"
              className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
            >
              Component Category
            </Typography>
          </Breadcrumbs>
          <Typography variant="body2" className="complaint-category-header-title">
            Manage component category master.
          </Typography>
        </Box>
        {canAdd && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/component-categories/add")}
            className="btn-add-complaint-category"
          >
            Add Component Category
          </Button>
        )}
      </Box>

      {/* Filter Panel */}
      <div className="card-panel mb-3 filter-panel">
        <div className="section-heading d-flex align-items-center gap-2 mb-3">
          <SearchIcon
            fontSize="small"
            className="text-muted search-icon"
          />
          <span>Filter: Name and Status</span>
        </div>

        <div className="container-fluid">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-6 col-lg-4 srchDvwrpr">
              <label className="input-label d-block mb-1 text-start">
                Search
              </label>
              <div className="input-icon">
                <input
                  type="text"
                  className="form-control form-control-light text-start"
                  placeholder="Search by Code / Name"
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleFilterChange("search", filters.search.trim());
                      fetchCategories(filters.search.trim(), filters.status);
                    }
                  }}
                />
                <SearchIcon className="icon-right" />
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">
                Status
              </label>
              <select
                ref={statusSelectRef}
                id="status-filter-select"
                className="form-select form-control-light filter-select text-start"
                value={filters.status}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start label-hidden">
                Actions
              </label>
              <div className="d-flex gap-2 filter-buttons justify-content-start">
                <button
                  type="button"
                  className="btn btn-filter"
                  onClick={() => {
                    handleFilterChange("search", filters.search.trim());
                    fetchCategories(filters.search.trim(), filters.status);
                  }}
                >
                  <SearchIcon fontSize="small" className="me-1" />
                  Search
                </button>
                <button
                  type="button"
                  className="btn btn-reset-filter"
                  onClick={resetFilters}
                  title="Reset"
                >
                  <RestartAltIcon fontSize="small" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Paper className="complaint-category-paper">
        <div className="card-panel mt-0">
          {loading && categories.length === 0 ? (
            <Box className="loading-state">
              <Typography>Loading complaint categories...</Typography>
            </Box>
          ) : categories.length === 0 ? (
            <Box className="empty-state">
              <Typography variant="body1" color="text.secondary">
                No complaint categories found.{" "}
                {canAdd && 'Click "Add Component Category" to create a new component category.'}
              </Typography>
            </Box>
          ) : (
            <div className="table-responsive">
              <table
                ref={tableRef}
                className="table table-striped table-bordered complaint-table custmTblLst complaint-category-table"
              />
            </div>
          )}
        </div>
      </Paper>
    </Box>
  );
};

export default ComplaintCategoryMasterManagement;
